import { getOrCreateAuthSessionMarker } from 'utils/auth/authSessionMarker';

const DATABASE_PREFIX = 'nucleai-download-';
const DATABASE_LOCK_PREFIX = 'nucleai-download-lock-';
const CHUNKS_STORE = 'chunks';
export const INDEXED_DB_BLOB_CHUNK_SIZE = 8 * 1024 * 1024;

interface StoredBlobChunk {
  index: number;
  blob: Blob;
}

const requestToPromise = <Result>(request: IDBRequest<Result>): Promise<Result> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });

const transactionToPromise = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction was aborted'));
  });

const openDatabase = (indexedDb: IDBFactory, databaseName: string): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDb.open(databaseName, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(CHUNKS_STORE, { keyPath: 'index' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB is unavailable'));
    request.onblocked = () => reject(new Error('IndexedDB operation was blocked'));
  });

const deleteDatabase = (indexedDb: IDBFactory, databaseName: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const request = indexedDb.deleteDatabase(databaseName);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error('IndexedDB cleanup failed'));
    request.onblocked = () => reject(new Error('IndexedDB cleanup was blocked'));
  });

const getDatabaseLockName = (databaseName: string) => `${DATABASE_LOCK_PREFIX}${databaseName}`;

const acquireDatabaseLock = (
  locks: LockManager | undefined,
  databaseName: string
): Promise<(() => Promise<void>) | undefined> => {
  if (!locks) {
    return Promise.resolve(undefined);
  }

  return new Promise((resolve, reject) => {
    let releaseLock: () => void = () => undefined;
    const holdLock = new Promise<void>((release) => {
      releaseLock = release;
    });
    let lockRequest: Promise<void>;
    lockRequest = locks.request(getDatabaseLockName(databaseName), async () => {
      resolve(async () => {
        releaseLock();
        await lockRequest;
      });
      await holdLock;
    });
    lockRequest.catch(reject);
  });
};

export interface IndexedDbBlobStore {
  databaseName: string;
  writable: WritableStream<Uint8Array>;
  getBlob: (type?: string) => Promise<Blob>;
  cleanup: () => Promise<void>;
}

export const createIndexedDbBlobStore = async (
  operationId: string,
  indexedDb: IDBFactory | undefined = window.indexedDB,
  locks: LockManager | undefined = navigator.locks,
  authSessionMarker = getOrCreateAuthSessionMarker()
): Promise<IndexedDbBlobStore> => {
  if (!indexedDb) {
    throw new Error('IndexedDB is unavailable');
  }

  await cleanupStaleIndexedDbBlobStores(indexedDb, locks);
  // The marker identifies the shared login session; the operation lock below is the deletion-safety boundary.
  const databaseName = `${DATABASE_PREFIX}${authSessionMarker}-${operationId}`;
  const releaseDatabaseLock = await acquireDatabaseLock(locks, databaseName);
  let database: IDBDatabase;
  try {
    database = await openDatabase(indexedDb, databaseName);
  } catch (error) {
    await releaseDatabaseLock?.();
    throw error;
  }
  let nextChunkIndex = 0;

  const writeBlob = async (blob: Blob) => {
    const transaction = database.transaction(CHUNKS_STORE, 'readwrite');
    transaction.objectStore(CHUNKS_STORE).put({ index: nextChunkIndex, blob } as StoredBlobChunk);
    nextChunkIndex += 1;
    await transactionToPromise(transaction);
  };

  return {
    databaseName,
    writable: new WritableStream<Uint8Array>({
      write: async (chunk) => {
        for (let offset = 0; offset < chunk.byteLength; offset += INDEXED_DB_BLOB_CHUNK_SIZE) {
          await writeBlob(new Blob([chunk.slice(offset, offset + INDEXED_DB_BLOB_CHUNK_SIZE)]));
        }
      },
    }),
    getBlob: async (type = 'application/octet-stream') => {
      const transaction = database.transaction(CHUNKS_STORE, 'readonly');
      const chunks = await requestToPromise<StoredBlobChunk[]>(transaction.objectStore(CHUNKS_STORE).getAll());
      await transactionToPromise(transaction);
      return new Blob(
        chunks.sort((left, right) => left.index - right.index).map(({ blob }) => blob),
        { type }
      );
    },
    cleanup: async () => {
      database.close();
      try {
        await deleteDatabase(indexedDb, databaseName);
      } finally {
        await releaseDatabaseLock?.();
      }
    },
  };
};

export const cleanupStaleIndexedDbBlobStores = async (
  indexedDb: IDBFactory | undefined = window.indexedDB,
  locks: LockManager | undefined = navigator.locks
): Promise<void> => {
  // Web Locks make database enumeration and deletion safe while other tabs may be exporting.
  if (!indexedDb?.databases || !locks) {
    return;
  }

  const databases = await indexedDb.databases();
  await Promise.all(
    databases
      .map(({ name }) => name)
      .filter((name): name is string => Boolean(name?.startsWith(DATABASE_PREFIX)))
      .map((name) =>
        locks.request(getDatabaseLockName(name), { ifAvailable: true }, async (lock) => {
          if (lock) {
            await deleteDatabase(indexedDb, name);
          }
        })
      )
  );
};
